import React, { useState, useEffect, useMemo } from "react";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Container, Row, Col, Form, FormGroup, Label, Input, Button, Card, CardBody, Modal,ModalHeader,ModalBody, ModalFooter, Popover,
    PopoverHeader,
    PopoverBody } from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";
import moment from 'moment';
import Loader from "../../Components/Common/Loader";
import Select, { components } from "react-select";

const dayValue = (date) => moment(date).startOf("day").valueOf();

const rangesOverlap = (aFrom, aTo, bFrom, bTo) => {
  if (!aFrom || !aTo || !bFrom || !bTo) return false;
  return dayValue(aFrom) <= dayValue(bTo) && dayValue(bFrom) <= dayValue(aTo);
};

const isInsideRange = (date, from, to) => {
  if (!date || !from || !to) return false;
  const day = dayValue(date);
  return day >= dayValue(from) && day <= dayValue(to);
};

const disabledPeriod = (from, to) => {
  if (!from || !to) return [];
  return [{
    from: moment(from).startOf("day").toDate(),
    to: moment(to).startOf("day").toDate(),
  }];
};

const dateKey = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

// Latest end date that keeps this period entirely before the other one.
// A start after the other period has no upper bound.
const periodEndMaxDate = (fromDate, otherFrom, otherTo) => {
  if (!fromDate || !otherFrom || !otherTo) return null;
  if (dayValue(fromDate) < dayValue(otherFrom)) {
    return moment(otherFrom).startOf("day").subtract(1, "day").toDate();
  }
  return null;
};

const buildEndOptions = (fromDate, otherFrom, otherTo) => {
  const options = {
    dateFormat: "d-m-Y",
    minDate: fromDate || null,
  };

  if (isInsideRange(fromDate, otherFrom, otherTo)) {
    options.disable = [{ from: new Date(2000, 0, 1), to: new Date(2100, 11, 31) }];
    return options;
  }

  const maxDate = periodEndMaxDate(fromDate, otherFrom, otherTo);
  if (maxDate) options.maxDate = maxDate;
  return options;
};

const isVariableFilters = (filters) => {
  const value = filters?.variable_filters;
  return value === 1 || value === "1" || value === true;
};

const formatDateRange = (from, to) => {
  if (!from || !to) return "";
  const start = moment(from);
  const end = moment(to);
  if (!start.isValid() || !end.isValid()) return "";
  return `${start.format("YYYY-MM-DD")} to ${end.format("YYYY-MM-DD")}`;
};

const formatClock = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const parsed = moment(value, ["HH:mm", "HH:mm:ss"], true);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  }
  return moment(value).isValid() ? moment(value).format("HH:mm") : "";
};

const formatHourRange = (from, to) => {
  const start = formatClock(from);
  const end = formatClock(to);
  if (!start || !end) return "";
  return `${start} - ${end}`;
};

const measurementSummaryRows = (type, measurementText, selectedItemText) => {
  if (type === "Individual Items") {
    return [
      { label: "Measurement Type", value: type },
      { label: "Selected Course", value: measurementText },
      { label: "Specific Measurement", value: selectedItemText },
    ];
  }
  return [
    { label: "Measurement Type", value: type },
    {
      label: type === "Promotion" ? "Discount Type" : "Specific Measurement",
      value: measurementText,
    },
  ];
};

const mergeSummaryRows = (testingRows, controlRows) => {
  const controlByLabel = new Map(controlRows.map((row) => [row.label, row.value]));
  const seen = new Set();
  const merged = testingRows.map((row) => {
    seen.add(row.label);
    return {
      label: row.label,
      testing: row.value,
      control: controlByLabel.has(row.label) ? controlByLabel.get(row.label) : "",
    };
  });
  controlRows.forEach((row) => {
    if (!seen.has(row.label)) {
      merged.push({ label: row.label, testing: "", control: row.value });
    }
  });
  return merged;
};

const SideBySideSummary = ({ testingRows, controlRows }) => {
  const rows = mergeSummaryRows(testingRows, controlRows);
  return (
    <div className="table-responsive">
      <table className="table table-bordered align-middle mb-0">
        <thead>
          <tr>
            <th style={{ width: "28%", background: "#F3F6F9" }}></th>
            <th style={{ background: "#F3F6F9" }}>Testing Period</th>
            <th style={{ background: "#F3F6F9" }}>Control Period</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td className="fw-semibold">{row.label}</td>
              <td>{row.testing || "Not specified"}</td>
              <td>{row.control || "Not specified"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const summaryRowsFromFilters = (filters, side, createdBy) => {
  const useControl = side === "control" && isVariableFilters(filters);
  const type = useControl
    ? (filters.control_measurement_type || "")
    : (filters.measurement_type || "");
  const specific = useControl
    ? (filters.controlMeasurementText || filters.control_measurement_text || "")
    : (filters.measurementText || "");
  const items = useControl
    ? (filters.controlSelectedItemText || filters.control_selected_item_text || "")
    : (filters.selectedItemText || "");
  const serving = useControl
    ? (filters.controlServingPeriodText || filters.control_serving_period_text || "")
    : (filters.servingPeriodText || "");
  const days = useControl
    ? (filters.control_selected_days || [])
    : (filters.selected_days || []);
  const fromHour = useControl ? filters.control_from_time : filters.from_time;
  const toHour = useControl ? filters.control_to_time : filters.to_time;
  const range = side === "control"
    ? formatDateRange(filters.control_from, filters.control_to)
    : formatDateRange(filters.test_from, filters.test_to);

  return [
    { label: "Action", value: filters.action || "" },
    { label: "Revenue Pillar", value: filters.revenuePillar || "" },
    { label: "Period", value: range },    
    ...measurementSummaryRows(type, specific, items),
    { label: "Serving Period", value: serving || "" },
    { label: "Days", value: days.length ? days.join(", ") : "" },
    { label: "Hours", value: formatHourRange(fromHour, toHour) },
    { label: "Created By", value: createdBy || "" },
  ];
};

export default function RoiCalculation() {
  const [step, setStep] = useState(1);
  const [revenuePillarsDD, setRevenuePillarsDD] = useState({});
  const [measurementTypeDD, setMeasurementTypeDD] = useState("");
  const [coursesDD, setCoursesDD] = useState({});
  const [KPIsDD, setKPIsDD] = useState({});
  const [mealPeriodDD, setMealPeriodDD] = useState([]);
  const [measurement, setMeasurement] = useState("");
  const [measurementText, setMeasurementText] = useState("");
  const [selectedMenu, setSelectedMenu] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedItemText, setSelectedItemText] = useState("");
  const [itemsDD, setItemsDD] = useState([]);
  const [discountCodesDD, setDiscountCodesDD] = useState([]);
  const [currency, setCurrency] = useState('$');
  const [currentUser, setCurrentUser] = useState({});
  const [itemPopover, setItemPopover] = useState(null);
  const { courses, mealperiod, currentOutlet, userInfo } = useSelector((state) => ({
      courses: state.Dashboard.filterData.courses || [],
      mealperiod: state.Dashboard.filterData.mealperiod,
      currentOutlet: state.UserOutletGroup.currentOutlet,
      userInfo: state.UserOutletGroup?.userInfo,
  }));
  
  useEffect(()=>{
      setCoursesDD(courses);
      setMealPeriodDD(mealperiod);
      setCurrentUser(userInfo);
  },[courses,mealperiod, userInfo])
  useEffect(() => {
    if (currentOutlet?.currency !== "") {
      setCurrency(currentOutlet?.currency);
    }
  }, [currentOutlet]);
  
  const [measurementType, setMeasurementType] = useState("");
  const [testFrom, setTestFrom] = useState(null);
  const [testTo, setTestTo] = useState(null);
  const [controlFrom, setControlFrom] = useState(null);
  const [controlTo, setControlTo] = useState(null);
  const [servingPeriod, setServingPeriod] = useState("");
  const [servingPeriodText, setServingPeriodText] = useState("");
  const [selectedDays, setSelectedDays] = useState(["All"]); 

  const [fromTime, setFromTime] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [variableFilters, setVariableFilters] = useState(false);
  const [controlMeasurementType, setControlMeasurementType] = useState("");
  const [controlMeasurement, setControlMeasurement] = useState("");
  const [controlMeasurementText, setControlMeasurementText] = useState("");
  const [controlSelectedMenu, setControlSelectedMenu] = useState([]);
  const [controlSelectedItem, setControlSelectedItem] = useState("");
  const [controlSelectedItemText, setControlSelectedItemText] = useState("");
  const [controlItemsDD, setControlItemsDD] = useState([]);
  const [controlServingPeriod, setControlServingPeriod] = useState("");
  const [controlServingPeriodText, setControlServingPeriodText] = useState("");
  const [controlSelectedDays, setControlSelectedDays] = useState(["All"]);
  const [controlFromTime, setControlFromTime] = useState(null);
  const [controlToTime, setControlToTime] = useState(null);

  const [action, setAction] = useState("");
  const [revenuePillar, setRevenuePillar] = useState("");
  const [showROIList, setShowROIList] = useState(true);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roiList, setRoiList] = useState([]);
  const [selectedROI, setSelectedROI] = useState(null);
  const [editROI, setEditROI] = useState(null);
  const [refreshList, setRefreshList] = useState(true);
  const [listLoading, setListLoading] = useState(false);


  const [roiData, setRoiData] = useState({
    targetQty: 0,
    targetRevenue: 0,
    avgCheck: 0,
    controlQty: 0,
    controlRevenue: 0,
    controlAvgCheck: 0,
    roi: 0,
    percentIncrease: 0,
  });


const calculateROI = () => {
  let targetQty = 0; 
  let targetRevenue = 0;
  let avgCheck = 0;

  let controlQty = 0;
  let controlRevenue = 0;
  let controlAvgCheck = 0;

  let targetTableTurnTime = 0;
  let controlTableTurnTime = 0;
  
  let targetTableOccupancy = 0;
  let controlTableOccupancy = 0;
  let controlCovers = 0;
  let targetCovers = 0;
  
    const activeControlMeasurementType = variableFilters ? controlMeasurementType : measurementType;
    const activeControlMeasurement = variableFilters ? controlMeasurement : measurement;
    const activeControlMeasurementText = variableFilters ? controlMeasurementText : measurementText;
    const activeControlSelectedItem = variableFilters ? controlSelectedItem : selectedItem;
    const activeControlSelectedItemText = variableFilters ? controlSelectedItemText : selectedItemText;
    const activeControlServingPeriod = variableFilters ? controlServingPeriod : servingPeriod;
    const activeControlServingPeriodText = variableFilters ? controlServingPeriodText : servingPeriodText;
    const activeControlSelectedDays = variableFilters ? controlSelectedDays : selectedDays;
    const activeControlFromTime = variableFilters ? controlFromTime : fromTime;
    const activeControlToTime = variableFilters ? controlToTime : toTime;

    if (!action || !revenuePillar || !measurementType || !measurement || !controlFrom || !controlTo || !testFrom || !testTo || (variableFilters && (!controlMeasurementType || !controlMeasurement))) {
      alert("Please fill all required fields!");
      return;
    }
    const formattedTestFrom    = testFrom    ? moment(testFrom).format("YYYY-MM-DD") : "";
    const formattedTestTo      = testTo      ? moment(testTo).format("YYYY-MM-DD") : "";
    const formattedControlFrom = controlFrom ? moment(controlFrom).format("YYYY-MM-DD") : "";
    const formattedControlTo   = controlTo   ? moment(controlTo).format("YYYY-MM-DD") : "";
    const formattedFromTime   = fromTime   ? moment(fromTime).format("HH:mm") : "";
    const formattedToTime  = toTime   ? moment(toTime).format("HH:mm") : "";
    const formattedControlFromTime = activeControlFromTime ? moment(activeControlFromTime).format("HH:mm") : "";
    const formattedControlToTime = activeControlToTime ? moment(activeControlToTime).format("HH:mm") : "";
    const additional_data = [action, revenuePillar, servingPeriodText, selectedItemText, measurementText]
    setLoading(true);
    const controller = new AbortController();
    postDataAPI({
        serviceName: 'kpi_data',
        op: 'roi_calculation',
        testFrom: formattedTestFrom,
        testTo: formattedTestTo,
        controlFrom: formattedControlFrom,
        controlTo: formattedControlTo,
        servingPeriod,
        selectedDays,
        fromTime: formattedFromTime,
        toTime: formattedToTime,
        measurement,
        selectedItem,
        measurementType,
        additional_data,
        variable_filters: variableFilters ? 1 : 0,
        control_measurement: activeControlMeasurement,
        control_measurement_type: activeControlMeasurementType,
        controlMeasurementText: activeControlMeasurementText,
        control_selected_item: activeControlSelectedItem,
        controlSelectedItemText: activeControlSelectedItemText,
        control_serving_period: activeControlServingPeriod,
        controlServingPeriodText: activeControlServingPeriodText,
        control_selected_days: activeControlSelectedDays,
        control_from_time: formattedControlFromTime,
        control_to_time: formattedControlToTime,
    }, controller.signal).then((response) => {
        setLoading(false);
        if (response.status === "ok") {
          controlQty = Number(response.data?.control_result?.item_sold) || 0;
          controlRevenue = Number(response.data?.control_result?.total_sales) || 0;
          controlCovers = Number(response.data?.control_result?.total_covers) || 0;
          if (controlQty) {
            controlAvgCheck = (controlRevenue/controlQty);
          }

          targetQty = Number(response.data?.test_result?.item_sold) || 0;
          targetRevenue = Number(response.data?.test_result?.total_sales) || 0;
          targetCovers = Number(response.data?.test_result?.total_covers) || 0;
          if (targetQty) {
            avgCheck = (targetRevenue/targetQty);
          }

          if (measurement === "Table Turn" || activeControlMeasurement === "Table Turn") {
            const ttDurationControl = Number(response.data?.control_result?.duration) || 0;
            const ttChkCountControl = Number(response.data?.control_result?.chk_count) || 0;
            const ttDurationTest = Number(response.data?.test_result?.duration) || 0;
            const ttChkCountTest = Number(response.data?.test_result?.chk_count) || 0;
            if(ttChkCountControl>0){
              controlTableTurnTime = ttDurationControl / ttChkCountControl;
            }
            if(ttChkCountTest>0){
              targetTableTurnTime = ttDurationTest / ttChkCountTest;
            }
          }
          if (measurement === "Occupancy Rate" || activeControlMeasurement === "Occupancy Rate") {
            const cDuration = Number(response.data?.control_result?.duration) || 0;
            const tDuration = Number(response.data?.test_result?.duration) || 0;
            const ctbl_duration = Number(response.data?.control_result?.tbl_duration) || 0;
            const ttbl_duration = Number(response.data?.test_result?.tbl_duration) || 0;
            if (ctbl_duration > 0) {
              controlTableOccupancy = cDuration/(ctbl_duration)*100;
            }
            if (ttbl_duration > 0) {
              targetTableOccupancy =tDuration/(ttbl_duration)*100;
            }
          }

          let roi; 
          let percentIncrease;
          // if (measurement === "Covers") {
          //   roi = targetCovers - controlCovers;
          //   percentIncrease = controlCovers === 0 ? -100 : ((roi / controlCovers) * 100);
          // } else if (measurement === "Average Check") {
          //   roi = avgCheck - controlAvgCheck;
          //   percentIncrease = controlAvgCheck === 0 ? -100 : ((roi / controlAvgCheck) * 100);
          // } else if (measurement === "Table Turn") {
          //   roi = targetTableTurnTime - controlTableTurnTime;
          //   percentIncrease = controlTableTurnTime === 0 ? -100 : ((roi / controlTableTurnTime) * 100);
          // } else if (measurement === "Occupancy Rate") {
          //   roi = targetTableOccupancy - controlTableOccupancy;
          //   percentIncrease = controlTableOccupancy === 0 ? -100 : ((roi / controlTableOccupancy) * 100);
          // } else {
          //   roi = targetRevenue - controlRevenue;
          //   percentIncrease = controlRevenue === 0 ? -100 : ((roi / controlRevenue) * 100);
          // }
          roi = targetRevenue - controlRevenue;
          percentIncrease = controlRevenue === 0 ? -100 : ((roi / controlRevenue) * 100);

          
          setRoiData({
            targetQty,
            targetRevenue,
            avgCheck,
            controlQty,
            controlRevenue,
            controlAvgCheck,
            roi,
            percentIncrease,
            controlTableTurnTime,
            targetTableTurnTime,
            controlTableOccupancy,
            targetTableOccupancy,
            controlCovers,
            targetCovers
          });
          setShowROIList(true);
          setRefreshList(!refreshList);
        }
    });
    return () => {
        controller.abort();
    };
};


  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const toggleDaySelection = (day, days, setDays) => {
  if (day === "All") {
    setDays(["All"]);
  } else {
    let updated = days.includes("All")
      ? []
      : [...days];

    if (updated.includes(day)) {
      updated = updated.filter((d) => d !== day);
    } else {
      updated.push(day);
    }

    if (updated.length === 0) {
      updated = ["All"];
    }

    setDays(updated);
  }
};

  const handleServingPeriodChange = (value, setPeriod, setText, setFrom, setTo) => {
    const selected = mealPeriodDD.find((item) => item.sp_id === value);
    setPeriod(value);
    if (value === "custom") {
      setText("Custom");
      setFrom(null);
      setTo(null);
    } else {
      setText(selected?.title || "");
    }

    if (selected?.start_time && selected?.end_time) {
      const today = new Date().toISOString().split("T")[0];
      setFrom(new Date(`${today} ${selected.start_time}`));
      setTo(new Date(`${today} ${selected.end_time}`));
    }
  };

  const copyTestingFiltersToControl = () => {
    setControlMeasurementType(measurementType);
    setControlMeasurement(measurement);
    setControlMeasurementText(measurementText);
    setControlSelectedMenu(selectedMenu.map((item) => ({ ...item })));
    setControlSelectedItem(selectedItem);
    setControlSelectedItemText(selectedItemText);
    setControlItemsDD(itemsDD);
    setControlServingPeriod(servingPeriod);
    setControlServingPeriodText(servingPeriodText);
    setControlSelectedDays([...selectedDays]);
    setControlFromTime(fromTime ? new Date(fromTime) : null);
    setControlToTime(toTime ? new Date(toTime) : null);
  };

  const handleVariableFiltersChange = (checked) => {
    setVariableFilters(checked);
    if (checked) {
      copyTestingFiltersToControl();
    }
  };

useEffect(() => {
  const controller = new AbortController();
  postDataAPI({
        serviceName: 'kpi_data',
        op: 'kpi_filter'
    }, controller.signal).then((response) => {
        if (response.status === "ok") {
          setRevenuePillarsDD(response.data.revenue_pillar);
          setMeasurementTypeDD(response.data.measurement_type);
          setKPIsDD(response.data.dynamic_dropdown.Kpis);
          setDiscountCodesDD(response.data.discount_codes);
        }
    });
    return () => {
        controller.abort();
    };
}, []);

useEffect(() => {
  const controller = new AbortController();
  setListLoading(true);
  postDataAPI({
        serviceName: 'kpi_data',
        op: 'roi_calculation_list'
    }, controller.signal).then((response) => {
        setListLoading(false);
        if (response.status === "ok") {
          setRoiList(response.data);
        }
    });
    return () => {
        controller.abort();
    };
}, [refreshList]);

const statistics = useMemo(() => {
    return roiList.reduce(
        (acc, item) => {
            const calc =
                typeof item.roi_calculated_result === "string"
                    ? JSON.parse(item.roi_calculated_result)
                    : item.roi_calculated_result;

            acc.controlRevenue += Number(calc.controlRevenue || 0);
            acc.targetRevenue += Number(calc.targetRevenue || 0);

            return acc;
        },
        {
            controlRevenue: 0,
            targetRevenue: 0,
        }
    );
}, [roiList]);

const statisticTotalROI =
    statistics.targetRevenue - statistics.controlRevenue;

const statisticPercentGrowth =
    statistics.controlRevenue === 0
        ? -100
        : (statisticTotalROI / statistics.controlRevenue) * 100;

const loadItemsByCourse = (courseId, selectedIds="", target="test") => {
  if (!courseId) return;

  postDataAPI({
    serviceName: "item_data",
    op: "fetch_item_dropdown",
    course_id: courseId,
    limit: 700
  }).then((res)=>{
      if(res.status==="ok"){
          const setItems = target === "control" ? setControlItemsDD : setItemsDD;
          const setMenu = target === "control" ? setControlSelectedMenu : setSelectedMenu;
          setItems(res.data);
          if(selectedIds){
              const ids = selectedIds.split(",");
              const selected = res.data.filter(item =>
                  ids.includes(String(item.value))
              );
              setMenu(selected);
          }
      }
  });
};

const applyDateChange = (field, selectedDates) => {
  const picked = selectedDates?.[0] || null;

  if (field === "testFrom" && isInsideRange(picked, controlFrom, controlTo)) return;
  if (field === "controlFrom" && isInsideRange(picked, testFrom, testTo)) return;
  if (field === "testTo" && testFrom && picked && dayValue(picked) < dayValue(testFrom)) return;
  if (field === "controlTo" && controlFrom && picked && dayValue(picked) < dayValue(controlFrom)) return;
  if (field === "testTo" && rangesOverlap(testFrom, picked, controlFrom, controlTo)) return;
  if (field === "controlTo" && rangesOverlap(testFrom, testTo, controlFrom, picked)) return;

  const next = { testFrom, testTo, controlFrom, controlTo, [field]: picked };

  if (field === "testFrom" && next.testTo && picked && dayValue(next.testTo) < dayValue(picked)) {
    next.testTo = null;
  }
  if (field === "controlFrom" && next.controlTo && picked && dayValue(next.controlTo) < dayValue(picked)) {
    next.controlTo = null;
  }
  if (field === "testFrom" && rangesOverlap(next.testFrom, next.testTo, next.controlFrom, next.controlTo)) {
    next.testTo = null;
  }
  if (field === "controlFrom" && rangesOverlap(next.testFrom, next.testTo, next.controlFrom, next.controlTo)) {
    next.controlTo = null;
  }

  let clearedControl = false;
  let clearedTest = false;
  if (isInsideRange(next.controlFrom, next.testFrom, next.testTo)) {
    next.controlFrom = null;
    next.controlTo = null;
    clearedControl = true;
  } else if (isInsideRange(next.testFrom, next.controlFrom, next.controlTo)) {
    next.testFrom = null;
    next.testTo = null;
    clearedTest = true;
  }

  setTestFrom(next.testFrom);
  setTestTo(next.testTo);
  setControlFrom(next.controlFrom);
  setControlTo(next.controlTo);

  if (clearedControl) {
    alert("The control period overlapped the testing period, so those control dates were cleared. Choose a control period entirely before or after the testing period.");
  } else if (clearedTest) {
    alert("The testing period overlapped the control period, so those testing dates were cleared. The two periods cannot overlap.");
  }
};

const validateTestDates = () => {
  if (!testFrom || !testTo) {
    alert("Please fill in the testing period dates.");
    return false;
  }

  if (testTo < testFrom) {
    alert("Test To date must be greater than Test From date.");
    return false;
  }

  return true;
};

const validateStep2 = () => {
  if (!testFrom || !testTo || !controlFrom || !controlTo) {
    alert("Please fill in all date fields.");
    return false;
  }

  if (testTo < testFrom) {
    alert("Test To date must be greater than Test From date.");
    return false;
  }

  if (controlTo < controlFrom) {
    alert("Control To date must be greater than Control From date.");
    return false;
  }

  if (rangesOverlap(testFrom, testTo, controlFrom, controlTo)) {
    alert("The testing period and control period cannot overlap. The control period must sit entirely before or entirely after the testing period.");
    return false;
  }

  return true;
};

const validateDateRange = () => {
  // Convert inputs to Date objects
  const cFrom = new Date(controlFrom);
  const cTo = new Date(controlTo);
  const tFrom = new Date(testFrom);
  const tTo = new Date(testTo);

  // Calculate day counts (inclusive)
  const controlDays =
    Math.floor((cTo - cFrom) / (1000 * 60 * 60 * 24)) + 1;

  const testDays =
    Math.floor((tTo - tFrom) / (1000 * 60 * 60 * 24)) + 1;

  // Compare day count instead of dates
  if (controlDays !== testDays) {
    const proceed = window.confirm(
      `Warning: Period Length Mismatch\nThe control period (${controlDays} days) does not match the test period (${testDays} days).\nThis may affect the accuracy of your ROI calculations.\n\nDo you want to continue anyway?`
    );

    if (proceed) {
      nextStep(); // user confirmed → move ahead
    }
    return; // stop if rejected
  }

  // If identical day count → proceed directly
  nextStep();
};

const loadROIForEdit = (row) => {
  const filters = JSON.parse(row.roi_filters || "{}");

  // Fill step-1 fields
  setAction(filters.action || "");
  setRevenuePillar(filters.revenuePillar || "");

  // Fill step-2 fields (dates)
  // Always use moment to safely convert API dates for Flatpickr
  setTestFrom(filters.test_from ? moment(filters.test_from, "YYYY-MM-DD").toDate() : null);
  setTestTo(filters.test_to ? moment(filters.test_to, "YYYY-MM-DD").toDate() : null);

  setControlFrom(filters.control_from ? moment(filters.control_from, "YYYY-MM-DD").toDate() : null);
  setControlTo(filters.control_to ? moment(filters.control_to, "YYYY-MM-DD").toDate() : null);


  // Step-3 fields
  setMeasurementType(filters.measurement_type || "");
  setMeasurement(filters.measurement || "");
  setMeasurementText(filters.measurementText || "");
  // setSelectedItem(filters.selected_item || "");
  // setSelectedItemText(filters.selectedItemText || "");
  const ids = (filters.selected_item || "").split(",");
  const selectedMenus = itemsDD.filter(item =>
      ids.includes(String(item.value))
  );
  setSelectedMenu(selectedMenus);
  setSelectedItem(filters.selected_item || "");
  setSelectedItemText(filters.selectedItemText || "");

  // Step-4 fields
  setServingPeriod(filters.serving_period || "");
  setServingPeriodText(filters.servingPeriodText || "");
  setSelectedDays(filters.selected_days?.length ? filters.selected_days : ["All"]);

  setFromTime(filters.from_time ? moment(filters.from_time, "HH:mm").toDate() : null);
  setToTime(filters.to_time ? moment(filters.to_time, "HH:mm").toDate() : null);

  const savedVariableFilters = isVariableFilters(filters);
  setVariableFilters(savedVariableFilters);
  if (savedVariableFilters) {
    setControlMeasurementType(filters.control_measurement_type || "");
    setControlMeasurement(filters.control_measurement || "");
    setControlMeasurementText(filters.controlMeasurementText || filters.control_measurement_text || "");
    setControlSelectedItem(filters.control_selected_item || "");
    setControlSelectedItemText(filters.controlSelectedItemText || filters.control_selected_item_text || "");
    setControlServingPeriod(filters.control_serving_period || "");
    setControlServingPeriodText(filters.controlServingPeriodText || filters.control_serving_period_text || "");
    setControlSelectedDays(filters.control_selected_days?.length ? filters.control_selected_days : ["All"]);
    setControlFromTime(filters.control_from_time ? moment(filters.control_from_time, "HH:mm").toDate() : null);
    setControlToTime(filters.control_to_time ? moment(filters.control_to_time, "HH:mm").toDate() : null);
    if (filters.control_measurement_type === "Individual Items" && filters.control_measurement) {
      loadItemsByCourse(filters.control_measurement, filters.control_selected_item || "", "control");
    }
  }

  // Load items list if it's "Individual Items"
  if (filters.measurement_type === "Individual Items" && filters.measurement) {
    loadItemsByCourse(filters.measurement, filters.selected_item);
  }

  setStep(6);
  setEditROI(row);
  setShowROIList(false);
  
};

const resetROIForm = () => {
  setStep(1);
  setAction("");
  setRevenuePillar("");

  setMeasurementType("");
  setMeasurement("");
  setMeasurementText("");
  setSelectedItem("");
  setSelectedItemText("");

  setTestFrom(null);
  setTestTo(null);
  setControlFrom(null);
  setControlTo(null);

  setServingPeriod("");
  setServingPeriodText("");
  setSelectedDays(["All"]);

  setFromTime(null);
  setToTime(null);
  setVariableFilters(false);
  setControlMeasurementType("");
  setControlMeasurement("");
  setControlMeasurementText("");
  setControlSelectedMenu([]);
  setControlSelectedItem("");
  setControlSelectedItemText("");
  setControlItemsDD([]);
  setControlServingPeriod("");
  setControlServingPeriodText("");
  setControlSelectedDays(["All"]);
  setControlFromTime(null);
  setControlToTime(null);

  setEditROI(null);        // clear edit mode
  setRoiData({});
};

const computeQtyPercentOfCovers = (calc) => {
  const targetCovers = Number(calc.targetCovers || 0);
  const actualCovers = Number(calc.controlCovers || 0);
  const targetQty = Number(calc.targetQty || 0);
  const actualQty = Number(calc.controlQty || 0);

  const targetRatio = targetCovers > 0 ? targetQty / targetCovers : null;
  const actualRatio = actualCovers > 0 ? actualQty / actualCovers : null;

  if (targetRatio === null || actualRatio === null || targetRatio === 0) {
    return null;
  }

  return (actualRatio / targetRatio) * 100 - 100;
};

const deleteROI = async (roiId) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this record?");
  if (!confirmDelete) return;

  try {
    postDataAPI({
        serviceName: 'kpi_data',
        op: 'roi_calculation_delete',
        row_id: roiId
    }).then((response) => {
        if (response.status === "ok") {
          setRefreshList(!refreshList);
        }
    });

  } catch (error) {
    console.error("Delete error:", error);
    alert("Something went wrong while deleting");
  }
};

const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <input type="checkbox" checked={props.isSelected} onChange={() => null} />
      <label style={{ marginLeft: "8px" }}>{props.data.label}</label>
    </components.Option>
  );
};

const normalizeItemSelection = (menus) => {
  const list = menus || [];
  const hasAll = list.some((item) => item.value === "");
  if (!hasAll) return list;

  const lastSelected = list[list.length - 1];
  if (lastSelected.value === "") {
    return [lastSelected];
  }
  return list.filter((item) => item.value !== "");
};

const renderWhatToMeasure = ({
  disabled,
  typeValue,
  measureValue,
  menuValue,
  itemOptions,
  onTypeChange,
  onMeasureChange,
  onItemsChange,
}) => (
  <Row>
    <Col md={6}>
      <FormGroup className="mb-3">
        <Label>Measurement Type*</Label>
        <Input
          type="select"
          required
          disabled={disabled}
          value={typeValue}
          onChange={(e) => onTypeChange(e.target.value)}
        >
          <option value="">Select what to measure</option>
          {Object.keys(measurementTypeDD).map((key) => (
            <option key={key} value={key}>
              {measurementTypeDD[key]}
            </option>
          ))}
        </Input>
      </FormGroup>
    </Col>

    {typeValue !== "" && (
      <Col md={6}>
        <FormGroup className="mb-3">
          <Label>
            {typeValue === "Courses"
              ? "Which Course*"
              : typeValue === "Individual Items"
              ? "Which Course*"
              : typeValue === "KPIs"
              ? "Which KPI*"
              : "Discount Type*"}
          </Label>
          <Input
            type="select"
            required
            disabled={disabled}
            value={measureValue}
            onChange={(e) => {
              const selected = e.target.value;
              const selectedLabel = e.target.options[e.target.selectedIndex].text;
              onMeasureChange(selected, selectedLabel);
            }}
          >
            {(typeValue === "Courses" || typeValue === "Individual Items") && (
              <>
                <option value="">Select course</option>
                {coursesDD.length > 0 &&
                  coursesDD.map((course) => (
                    <option key={course.value} value={course.value}>
                      {course.label}
                    </option>
                  ))}
              </>
            )}
            {typeValue === "KPIs" && (
              <>
                <option value="">Select KPI</option>
                {Object.keys(KPIsDD).map((key) => (
                  <option key={key} value={key}>
                    {KPIsDD[key]}
                  </option>
                ))}
              </>
            )}
            {typeValue === "Promotion" && (
              <>
                <option value="">Select discount</option>
                {discountCodesDD.length > 0 &&
                  discountCodesDD.map((d, index) => (
                    <option key={index} value={d.discount_name}>
                      {d.discount_name}
                    </option>
                  ))}
              </>
            )}
          </Input>
        </FormGroup>
      </Col>
    )}

    {typeValue === "Individual Items" && measureValue !== "" && (
      <Col md={6}>
        <FormGroup className="mb-3">
          <Label>Which Item*</Label>
          <Select
            options={itemOptions}
            isMulti
            isDisabled={disabled}
            closeMenuOnSelect={false}
            value={menuValue}
            components={{ Option: CustomOption }}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select Items"
            onChange={(menus) => onItemsChange(normalizeItemSelection(menus))}
          />
        </FormGroup>
      </Col>
    )}
  </Row>
);

const renderFineTuning = ({
  disabled,
  idPrefix,
  servingValue,
  daysValue,
  fromValue,
  toValue,
  onServingChange,
  onDayToggle,
  onFromChange,
  onToChange,
}) => (
  <>
    <FormGroup className="mb-4">
      <Label>Serving Period</Label>
      <Input
        type="select"
        disabled={disabled}
        value={servingValue}
        onChange={(e) => onServingChange(e.target.value)}
      >
        <option value="">Select serving period</option>
        {mealPeriodDD.length > 0 &&
          mealPeriodDD.map((item) => (
            <option key={item.sp_id} value={item.sp_id}>
              {item.title + " (" + item.start_time + " To " + item.end_time + ")"}
            </option>
          ))}
        <option value="custom">Custom</option>
      </Input>
    </FormGroup>

    <Label className="mb-2">Day of Week</Label>
    <div className="d-flex flex-wrap mb-4">
      {["All", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
        <FormGroup check className="me-3" key={`${idPrefix}-${day}`}>
          <Input
            type="checkbox"
            id={`${idPrefix}-day-${day}`}
            disabled={disabled}
            checked={daysValue.includes(day)}
            onChange={() => onDayToggle(day)}
          />
          <Label check htmlFor={`${idPrefix}-day-${day}`}>{day}</Label>
        </FormGroup>
      ))}
    </div>

    <Label className="mb-2">Hours Filter (Optional)</Label>
    <Row className="mb-4">
      <Col md={6}>
        <FormGroup>
          <Label>From</Label>
          <Flatpickr
            key={`${idPrefix}-from-${disabled ? "locked" : "edit"}`}
            className="form-control"
            disabled={disabled}
            value={fromValue}
            onChange={(t) => onFromChange(t[0])}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true,
              clickOpens: !disabled,
            }}
            placeholder="Select time"
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label>To</Label>
          <Flatpickr
            key={`${idPrefix}-to-${disabled ? "locked" : "edit"}`}
            className="form-control"
            disabled={disabled}
            value={toValue}
            onChange={(t) => onToChange(t[0])}
            options={{
              enableTime: true,
              noCalendar: true,
              dateFormat: "H:i",
              time_24hr: true,
              clickOpens: !disabled,
            }}
            placeholder="Select time"
          />
        </FormGroup>
      </Col>
    </Row>
  </>
);

  const testFromOptions = useMemo(() => ({
    dateFormat: "d-m-Y",
    disable: disabledPeriod(controlFrom, controlTo),
  }), [controlFrom, controlTo]);

  const testToOptions = useMemo(
    () => buildEndOptions(testFrom, controlFrom, controlTo),
    [testFrom, controlFrom, controlTo]
  );

  const controlFromOptions = useMemo(() => ({
    dateFormat: "d-m-Y",
    disable: disabledPeriod(testFrom, testTo),
  }), [testFrom, testTo]);

  const controlToOptions = useMemo(
    () => buildEndOptions(controlFrom, testFrom, testTo),
    [controlFrom, testFrom, testTo]
  );

  const createdByName = (currentUser?.first_name || currentUser?.last_name)
    ? `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`.trim()
    : "-";
  const controlMeasureLocked = !variableFilters;
  const summaryControlType = variableFilters ? controlMeasurementType : measurementType;
  const summaryControlSpecific = variableFilters ? controlMeasurementText : measurementText;
  const summaryControlItems = variableFilters ? controlSelectedItemText : selectedItemText;
  const summaryControlServing = variableFilters ? controlServingPeriodText : servingPeriodText;
  const summaryControlDays = variableFilters ? controlSelectedDays : selectedDays;
  const summaryControlFromTime = variableFilters ? controlFromTime : fromTime;
  const summaryControlToTime = variableFilters ? controlToTime : toTime;
  const revenuePillarLabel = revenuePillarsDD[revenuePillar] || revenuePillar;

  const testingSummaryRows = [
    { label: "Period", value: formatDateRange(testFrom, testTo) },
    { label: "Action", value: action },
    { label: "Revenue Pillar", value: revenuePillarLabel },
    ...measurementSummaryRows(measurementType, measurementText, selectedItemText),
    { label: "Serving Period", value: servingPeriodText },
    { label: "Days", value: selectedDays.length ? selectedDays.join(", ") : "" },
    { label: "Hours", value: formatHourRange(fromTime, toTime) },
    { label: "Created By", value: createdByName },
  ];

  const controlSummaryRows = [
    { label: "Period", value: formatDateRange(controlFrom, controlTo) },
    { label: "Action", value: action },
    { label: "Revenue Pillar", value: revenuePillarLabel },
    ...measurementSummaryRows(summaryControlType, summaryControlSpecific, summaryControlItems),
    { label: "Serving Period", value: summaryControlServing },
    { label: "Days", value: summaryControlDays.length ? summaryControlDays.join(", ") : "" },
    { label: "Hours", value: formatHourRange(summaryControlFromTime, summaryControlToTime) },
    { label: "Created By", value: createdByName },
  ];

  return (
    <React.Fragment>
          <div className="page-content">
            <Container fluid>
              <BreadCrumb title="ROI Calculations" pageTitle="Dashboard" />
              {listLoading && <Loader />}
              {!showROIList && (
              <div>
              <Row className="justify-content-center">
                <Col>
                  <Card>
                    <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h2 className="mb-0">ROI Calculations</h2>

                      <div
                        className="d-flex align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowROIList(true)}
                      >
                        <i className="ri-arrow-left-line me-2"></i>
                        <span>Back to List</span>
                      </div>
                    </div>


                    {/* Step 1 */}
                    <div style={{ display: step >= 1 ? "block" : "none", paddingTop:"30px" }} className="ribbon-box">
                        <div className="ribbon ribbon-danger round-shape">Define Action & Revenue Pillar</div>
                        <div className="ribbon-content mt-4 text-muted">
                    <Row>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                        <Label>Action*</Label>
                        {/* <Input type="text" placeholder="e.g., Increase tea by 1" defaultValue={action} onBlur={(e) => setAction(e.target.value)} /> */}
                        <Input
                          type="text"
                          placeholder="e.g., Increase tea by 1"
                          value={action}
                          onChange={(e) => setAction(e.target.value)}
                        />

                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-3">
                          <Label>Revenue Pillar*</Label>
                          <Input type="select" required
                                value={revenuePillar}
                                onChange={(e) => setRevenuePillar(e.target.value)}>
                            <option value="">Select revenue pillar</option>
                            {Object.keys(revenuePillarsDD).map((key) => (
                              <option key={key} value={key}>
                                {revenuePillarsDD[key]}
                              </option>
                            ))}

                          </Input>
                        </FormGroup>
                      </Col>
                      </Row>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="select-variable-filters"
                        checked={variableFilters}
                        onChange={(e) => handleVariableFiltersChange(e.target.checked)}
                      />
                      <Label check htmlFor="select-variable-filters" className="fw-medium">
                        Select variable filters
                      </Label>
                      <small className="d-block mt-1">
                        Check this to choose measurement and fine-tuning separately for the testing and control periods. Leave it unchecked and the control period keeps the testing period filters.
                      </small>
                    </FormGroup>
                    {step === 1 && (
                        <div className="d-flex justify-content-end">
                        <Button color="danger" onClick={nextStep}>Next</Button>
                        </div>
                    )}
                    </div>
                    </div>

                                {/* Step 2 */}
                                {/* Step 2 */}
                    <div
                      style={{ display: step >= 2 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                       <div className="ribbon ribbon-primary round-shape">Define the Target Timeframe</div>

                      <div className="ribbon-content mt-4 text-muted">

                        {/* --------------------------- */}
                        {/* TESTING PERIOD SECTION      */}
                        {/* --------------------------- */}
                        <h6 className="mb-3">Testing Period (Future or Historical)</h6>

                        <Row className="mb-4">
                          <Col md={6}>
                            <FormGroup>
                              <Label>From*</Label>
                              <Flatpickr
                                key={`test-from-${dateKey(controlFrom)}-${dateKey(controlTo)}`}
                                className="form-control"
                                placeholder="dd-mm-yyyy"
                                value={testFrom}
                                onChange={(date) => applyDateChange("testFrom", date)}
                                options={testFromOptions}
                              />
                            </FormGroup>
                          </Col>

                          <Col md={6}>
                            <FormGroup>
                              <Label>To*</Label>
                              <Flatpickr
                                key={`test-to-${dateKey(testFrom)}-${dateKey(controlFrom)}-${dateKey(controlTo)}`}
                                className="form-control"
                                placeholder="dd-mm-yyyy"
                                value={testTo}
                                onChange={(date) => applyDateChange("testTo", date)}
                                options={testToOptions}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <hr />

                        {step === 2 && (
                          <div className="d-flex justify-content-between">
                            <Button color="secondary" onClick={prevStep}>
                              Previous
                            </Button>

                            <Button color="danger" onClick={() => {
                                if (validateTestDates()) {
                                  nextStep();
                                }
                              }}>
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                      
                    </div>



                                {/* Step 3 */}
                    <div
                      style={{ display: step >= 3 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">What to Measure</div>

                      <div className="ribbon-content mt-4 text-muted">
                        <small className="d-block mb-3">Testing period</small>
                        {renderWhatToMeasure({
                          disabled: false,
                          typeValue: measurementType,
                          measureValue: measurement,
                          menuValue: selectedMenu,
                          itemOptions: itemsDD,
                          onTypeChange: (value) => {
                            setMeasurementType(value);
                            setSelectedItemText("");
                            setMeasurement("");
                            setMeasurementText("");
                            setSelectedItem("");
                            setSelectedMenu([]);
                          },
                          onMeasureChange: (selected, selectedLabel) => {
                            setMeasurementText(selectedLabel);
                            setMeasurement(selected);
                            if (measurementType === "Individual Items") {
                              loadItemsByCourse(selected);
                            }
                          },
                          onItemsChange: (finalMenus) => {
                            setSelectedMenu(finalMenus);
                            setSelectedItem(finalMenus.map((item) => item.value).join(","));
                            setSelectedItemText(finalMenus.map((item) => item.label).join(", "));
                          },
                        })}

                        {step === 3 && (
                          <div className="d-flex justify-content-between">
                            <Button color="secondary" onClick={prevStep}>
                              Previous
                            </Button>
                            <Button color="danger" onClick={nextStep}>Next</Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{ display: step >= 4 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">Fine-Tuning & Filters</div>

                      <div className="ribbon-content mt-4 text-muted">
                        <small className="d-block mb-3">Testing period</small>
                        {renderFineTuning({
                          disabled: false,
                          idPrefix: "test",
                          servingValue: servingPeriod,
                          daysValue: selectedDays,
                          fromValue: fromTime,
                          toValue: toTime,
                          onServingChange: (value) => handleServingPeriodChange(
                            value,
                            setServingPeriod,
                            setServingPeriodText,
                            setFromTime,
                            setToTime
                          ),
                          onDayToggle: (day) => toggleDaySelection(day, selectedDays, setSelectedDays),
                          onFromChange: setFromTime,
                          onToChange: setToTime,
                        })}

                        {step === 4 && (
                          <div className="d-flex justify-content-between">
                            <Button color="secondary" onClick={prevStep}>
                              Previous
                            </Button>
                            <Button color="danger" onClick={nextStep}>
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{ display: step >= 5 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">Control Period</div>
                      <div className="ribbon-content mt-4 text-muted">
                        <h6 className="mb-1">Control Period (Future or Historical)</h6>
                        <small className="d-block mb-3">
                          Can sit before or after the testing period. The two periods cannot overlap.
                        </small>
                        <Row className="mb-2">
                          <Col md={6}>
                            <FormGroup>
                              <Label>From*</Label>
                              <Flatpickr
                                key={`control-from-${dateKey(testFrom)}-${dateKey(testTo)}`}
                                className="form-control"
                                placeholder="dd-mm-yyyy"
                                value={controlFrom}
                                onChange={(date) => applyDateChange("controlFrom", date)}
                                options={controlFromOptions}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>To*</Label>
                              <Flatpickr
                                key={`control-to-${dateKey(controlFrom)}-${dateKey(testFrom)}-${dateKey(testTo)}`}
                                className="form-control"
                                placeholder="dd-mm-yyyy"
                                value={controlTo}
                                onChange={(date) => applyDateChange("controlTo", date)}
                                options={controlToOptions}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    </div>

                    <div
                      style={{ display: step >= 5 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">What to Measure</div>
                      <div
                        className="ribbon-content mt-4 text-muted"
                        style={controlMeasureLocked ? { background: "#f3f4f6", borderRadius: "8px" } : undefined}
                      >
                        <small className="d-block mb-3">
                          {controlMeasureLocked
                            ? "Control period. These filters match the testing period and stay locked."
                            : "Control period. Choose what to measure for the control period."}
                        </small>
                        <div style={controlMeasureLocked ? { pointerEvents: "none" } : undefined}>
                          {renderWhatToMeasure({
                            disabled: controlMeasureLocked,
                            typeValue: controlMeasureLocked ? measurementType : controlMeasurementType,
                            measureValue: controlMeasureLocked ? measurement : controlMeasurement,
                            menuValue: controlMeasureLocked ? selectedMenu : controlSelectedMenu,
                            itemOptions: controlMeasureLocked ? itemsDD : controlItemsDD,
                            onTypeChange: (value) => {
                              setControlMeasurementType(value);
                              setControlMeasurement("");
                              setControlMeasurementText("");
                              setControlSelectedItem("");
                              setControlSelectedItemText("");
                              setControlSelectedMenu([]);
                            },
                            onMeasureChange: (selected, selectedLabel) => {
                              setControlMeasurementText(selectedLabel);
                              setControlMeasurement(selected);
                              setControlSelectedMenu([]);
                              setControlSelectedItem("");
                              setControlSelectedItemText("");
                              if (controlMeasurementType === "Individual Items") {
                                loadItemsByCourse(selected, "", "control");
                              }
                            },
                            onItemsChange: (finalMenus) => {
                              setControlSelectedMenu(finalMenus);
                              setControlSelectedItem(finalMenus.map((item) => item.value).join(","));
                              setControlSelectedItemText(finalMenus.map((item) => item.label).join(", "));
                            },
                          })}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{ display: step >= 5 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">Fine-Tuning & Filters</div>
                      <div
                        className="ribbon-content mt-4 text-muted"
                        style={controlMeasureLocked ? { background: "#f3f4f6", borderRadius: "8px" } : undefined}
                      >
                        <small className="d-block mb-3">
                          {controlMeasureLocked
                            ? "Control period. Serving period, days, and hours match the testing period."
                            : "Control period. Fine-tune the control period separately."}
                        </small>
                        <div style={controlMeasureLocked ? { pointerEvents: "none" } : undefined}>
                          {renderFineTuning({
                            disabled: controlMeasureLocked,
                            idPrefix: "control",
                            servingValue: controlMeasureLocked ? servingPeriod : controlServingPeriod,
                            daysValue: controlMeasureLocked ? selectedDays : controlSelectedDays,
                            fromValue: controlMeasureLocked ? fromTime : controlFromTime,
                            toValue: controlMeasureLocked ? toTime : controlToTime,
                            onServingChange: (value) => handleServingPeriodChange(
                              value,
                              setControlServingPeriod,
                              setControlServingPeriodText,
                              setControlFromTime,
                              setControlToTime
                            ),
                            onDayToggle: (day) => toggleDaySelection(day, controlSelectedDays, setControlSelectedDays),
                            onFromChange: setControlFromTime,
                            onToChange: setControlToTime,
                          })}
                        </div>

                        {step === 5 && (
                          <div className="d-flex justify-content-between">
                            <Button color="secondary" onClick={prevStep}>
                              Previous
                            </Button>
                            <Button color="danger" onClick={() => {
                              if (!validateStep2()) return;
                              if (variableFilters && (!controlMeasurementType || !controlMeasurement)) {
                                alert("Please choose what to measure for the control period.");
                                return;
                              }
                              validateDateRange();
                            }}>
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* STEP 6 — SUMMARY */}
                    <div
                      style={{ display: step >= 6 ? "block" : "none", paddingTop: "30px" }}
                      className="ribbon-box"
                    >
                      <div className="ribbon ribbon-primary round-shape">Summary</div>
                      <div className="ribbon-content mt-4">
                        <SideBySideSummary
                          testingRows={testingSummaryRows}
                          controlRows={controlSummaryRows}
                        />

                        <hr className="my-4" />

                        <div className="d-flex justify-content-between">
                          <Button color="secondary" onClick={prevStep}>
                            Previous
                          </Button>
                          <Button
                            color="danger"
                            disabled={loading}
                            onClick={calculateROI}
                          >
                            {loading ? "Loading..." : "Calculate ROI"}
                          </Button>
                        </div>
                      </div>
                    </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              </div>)}

              {showROIList && (
                // <Row className="justify-content-center">
                // <Col>
                  <Card>
                    <CardBody>                    
                    <div>
                            <Row className="align-items-center mb-4">
                              <Col xl={9} lg={9} md={8}>
                                <Row className="g-3">
                                  <Col sm={4}>
                                    <Card className="card-animate mb-0">
                                      <CardBody>
                                        <div className="d-flex align-items-center">
                                          <div className="flex-grow-1 overflow-hidden">
                                            <p className="fw-medium text-muted text-truncate mb-2">Scenarios</p>
                                            <div className="d-flex align-items-center mb-1">
                                              <h4 className="fs-4 flex-grow-1 mb-0">
                                                <span className="counter-value me-1">{roiList ? roiList.length : 0}</span>
                                              </h4>
                                            </div>
                                          </div>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col sm={4}>
                                    <Card className="card-animate mb-0">
                                      <CardBody>
                                        <div className="d-flex align-items-center">
                                          <div className="flex-grow-1 overflow-hidden">
                                            <p className="fw-medium text-muted text-truncate mb-2">Revenue Growth</p>
                                            <div className="d-flex align-items-center mb-1">
                                              <h4 className="fs-4 flex-grow-1 mb-0" style={{ color: Number(statisticPercentGrowth) < 0 ? "red" : "green" }}>
                                                <span className="counter-value me-1">{currency}{Math.round(statisticTotalROI)}</span>
                                              </h4>
                                            </div>
                                          </div>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                  <Col sm={4}>
                                    <Card className="card-animate mb-0">
                                      <CardBody>
                                        <div className="d-flex align-items-center">
                                          <div className="flex-grow-1 overflow-hidden">
                                            <p className="fw-medium text-muted text-truncate mb-2">Revenue Growth</p>
                                            <div className="d-flex align-items-center mb-1">
                                              <h4 className="fs-4 flex-grow-1 mb-0" style={{ color: Number(statisticPercentGrowth) < 0 ? "red" : "green" }}>
                                                <span className="counter-value me-1">{statisticPercentGrowth.toFixed(2)}%</span>
                                              </h4>
                                            </div>
                                          </div>
                                        </div>
                                      </CardBody>
                                    </Card>
                                  </Col>
                                </Row>
                              </Col>
                              <Col xl={3} lg={3} md={4} className="text-end mt-md-0 mt-3">
                                <Button
                                  color="primary"
                                  onClick={() => {
                                    resetROIForm();
                                    setShowROIList(false);   // show form
                                  }}
                                >
                                  + Add New
                                </Button>
                              </Col>
                            </Row>


                            {roiList && roiList.length > 0 && roiList.map((row, index) => {

                              const filters = JSON.parse(row.roi_filters || "{}");
                              const control = JSON.parse(row.roi_control_results || "{}");
                              const test = JSON.parse(row.roi_test_results || "{}");
                              const calc = JSON.parse(row.roi_calculated_result || "{}");

                              const measurement = filters.measurement;
                              const controlMeasurementName = isVariableFilters(filters)
                                ? (filters.control_measurement || measurement)
                                : measurement;
                              const showTableTurn = measurement === "Table Turn" || controlMeasurementName === "Table Turn";
                              const showOccupancy = measurement === "Occupancy Rate" || controlMeasurementName === "Occupancy Rate";
                              const extraMetricCols = (showTableTurn ? 1 : 0) + (showOccupancy ? 1 : 0);
                              const selectedItemText = filters.selectedItemText || "ALL";
                              const servingPeriodText = filters.servingPeriodText || "-";
                              const selectedItems =
                                  selectedItemText === "ALL"
                                      ? []
                                      : selectedItemText
                                            .split(",")
                                            .map(item => item.trim())
                                            .filter(Boolean);
                              let targetAvgCheck = 0;
                              const targeQty = Number(calc.targetQty) || 0;
                              const targeRevenue = Math.round(Number(calc.targetRevenue)) || 0;
                              if (targeQty) {
                                targetAvgCheck = (targeRevenue/targeQty);
                              }

                              let controlAvgCheck = 0;
                              const controlQty = Number(calc.controlQty) || 0;
                              const controlRevenue = Math.round(Number(calc.controlRevenue)) || 0;
                              if (controlQty) {
                                controlAvgCheck = (controlRevenue/controlQty);
                              }

                              let roi = targeRevenue - controlRevenue;
                              let percentIncrease = controlRevenue === 0 ? -100 : ((roi / controlRevenue) * 100);

                              return (
                                <>
                                <h3 className="mb-2 d-flex align-items-center gap-2">
                                  ROI – {filters.action}
                                  <i
                                    className="ri-information-line"
                                    style={{ cursor: "pointer", fontSize: "22px" }}
                                    onClick={() => {
                                      setSelectedROI({
                                        row,
                                        filters,
                                        control,
                                        test,
                                        calc
                                      });
                                      setShowSummaryModal(true);
                                    }}
                                  ></i>

                                </h3>
                                <div className="table-responsive mb-4">
                                <table className="table align-middle table-nowrap table-bordered mb-0">
                                <thead className="table-light">
                                    <tr style={{ textAlign: 'center',verticalAlign: "middle" }}>
                                      <th scope="col" rowSpan={2}>Item</th>
                                      <th scope="col" rowSpan={2}>FROM</th>
                                      <th scope="col" rowSpan={2}>TO</th>
                                      <th scope="col" rowSpan={2}>Serving <br />Period</th>
                                      <th scope="col" colSpan={5 + extraMetricCols}>Target</th>
                                      <th scope="col" colSpan={5 + extraMetricCols}>Acutal / Control period</th>
                                      <th scope="col" rowSpan={2}>ROI</th>
                                      <th scope="col" rowSpan={2}>%</th>
                                      <th scope="col" rowSpan={2}>Action</th>                                 
                                    </tr>
                                    <tr style={{ textAlign: 'center' }}>
                                        <th scope="col">Covers </th>
                                        <th scope="col">Qty </th>
                                        <th scope="col">Q%</th>
                                        <th scope="col">Revenue</th>
                                        <th scope="col">Avg. <br />Check</th>
                                        {showTableTurn && (
                                          <th scope="col">Table Turn</th>
                                        )}
                                        {showOccupancy && (
                                          <th scope="col">Occupancy %</th>
                                        )}
                                        <th scope="col">Covers </th>
                                        <th scope="col">Qty</th>
                                        <th scope="col">Q%</th>
                                        <th scope="col">Revenue</th>
                                        <th scope="col">Avg. <br />Check</th>
                                        {showTableTurn && (
                                          <th scope="col">Table Turn</th>
                                        )}
                                        {showOccupancy && (
                                          <th scope="col">Occupancy %</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                <tr key={index}>
                                  <td className="text-start">
                                    {selectedItems.length <= 1 ? (
                                      selectedItemText
                                    ) : (
                                      <div className="d-inline-flex align-items-center gap-1">
                                        <span>{selectedItems.length} Items</span>

                                        <i
                                          id={`item-info-${row.roi_id}`}
                                          className="ri-information-line text-primary"
                                          style={{
                                            cursor: "pointer",
                                            fontSize: "16px",
                                            lineHeight: 1
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setItemPopover(
                                              itemPopover === row.roi_id ? null : row.roi_id
                                            );
                                          }}
                                        ></i>

                                        <Popover
                                          placement="right"
                                          trigger="legacy"
                                          target={`item-info-${row.roi_id}`}
                                          isOpen={itemPopover === row.roi_id}
                                          toggle={() => setItemPopover(null)}
                                        >
                                          <PopoverHeader>Selected Items</PopoverHeader>

                                          <PopoverBody>
                                            <ul className="mb-0 ps-3">
                                              {selectedItems.map((item, i) => (
                                                <li key={i}>{item}</li>
                                              ))}
                                            </ul>
                                          </PopoverBody>
                                        </Popover>
                                      </div>
                                    )}
                                  </td>

                                  <td className="text-center">
                                    {filters.test_from ? moment(filters.test_from).format("YYYY-MM-DD") : ""}
                                  </td>
                                  <td className="text-center">
                                    {filters.test_to ? moment(filters.test_to).format("YYYY-MM-DD") : ""}
                                  </td>

                                  <td className="text-center">{servingPeriodText}</td>

                                  {/* Target */}
                                  <td className="text-center">{calc.targetCovers}</td>
                                  <td className="text-center">{Math.round(Number(calc.targetQty) || 0)}</td>
                                  <td className="text-center">{Number((calc.targetQty / calc.targetCovers) * 100 || 0).toFixed(2)}%</td>
                                  <td className="text-center">{currency}{targeRevenue || 0}</td>
                                  <td className="text-center">{currency}{Number(targetAvgCheck || 0).toFixed(2)}</td>

                                  {showTableTurn && (
                                    <td className="text-center">{Number(calc.targetTableTurnTime || 0).toFixed(2)}</td>
                                  )}

                                  {showOccupancy && (
                                    <td className="text-center">{Number(calc.targetTableOccupancy || 0).toFixed(2)}%</td>
                                  )}

                                  {/* Actual / Control */}
                                  <td className="text-center">{calc.controlCovers}</td>
                                  <td className="text-center">{Math.round(Number(calc.controlQty) || 0)}</td>
                                  <td className="text-center">{Number((calc.controlQty / calc.controlCovers) * 100 || 0).toFixed(2)}%</td>
                                  <td className="text-center">{currency}{controlRevenue || 0}</td>
                                  <td className="text-center">{currency}{Number(controlAvgCheck || 0).toFixed(2)}</td>

                                  {showTableTurn && (
                                    <td className="text-center">{Number(calc.controlTableTurnTime || 0).toFixed(2)}</td>
                                  )}

                                  {showOccupancy && (
                                    <td className="text-center">{Number(calc.controlTableOccupancy || 0).toFixed(2)}%</td>
                                  )}

                                  {/* ROI */}
                                  <td
                                    style={{ color: Number(roi || 0) < 0 ? "red" : "green" }}
                                    className="text-center"
                                  >
                                    {currency}{Number(roi || 0).toFixed(0)}
                                  </td>

                                  <td
                                    style={{ color: Number(percentIncrease) < 0 ? "red" : "green" }}
                                    className="text-center"
                                  >
                                    {Number(percentIncrease || 0).toFixed(1)}%
                                  </td>

                                  {/* Action */}
                                  <td className="text-center">
                                    {row.created_by === currentUser?.user_id ? (
                                    <>
                                    <i
                                        className="ri-edit-line me-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => loadROIForEdit(row)}
                                      ></i>
                                    <i
                                      className="ri-delete-bin-line text-danger"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => deleteROI(row.roi_id)}
                                    ></i>
                                    </>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                             
                                </tbody>

                              </table>
                              </div>
                              </>
                       );
                            })}
                    </div>
                    </CardBody>
                  </Card>
              //   </Col>
              // </Row>
            )}

      </Container>
      </div>
      <Modal size="xl" isOpen={showSummaryModal} toggle={() => setShowSummaryModal(false)}>
        <ModalHeader toggle={() => setShowSummaryModal(false)}>
          Summary
        </ModalHeader>

        <ModalBody>

        {selectedROI ? (
          <SideBySideSummary
            testingRows={summaryRowsFromFilters(
              {
                ...selectedROI.filters,
                revenuePillar: revenuePillarsDD[selectedROI.filters?.revenuePillar] || selectedROI.filters?.revenuePillar,
              },
              "test",
              selectedROI.row?.created_by_name || "-"
            )}
            controlRows={summaryRowsFromFilters(
              {
                ...selectedROI.filters,
                revenuePillar: revenuePillarsDD[selectedROI.filters?.revenuePillar] || selectedROI.filters?.revenuePillar,
              },
              "control",
              selectedROI.row?.created_by_name || "-"
            )}
          />
        ) : (
          <p>No summary data available</p>
        )}


        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setShowSummaryModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      </React.Fragment>
  );
}
